import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { usePhotoFeed } from "../../hooks/usePhotoFeed";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useReloadOnToken } from "../../hooks/useReloadOnToken";
import { UploadDropzone } from "../../components/UploadDropzone";
import { PhotoGrid } from "../../components/PhotoGrid";
import styles from "./GuestPage.module.css";

export function GuestPage() {
  const config = useAppConfig();
  const { photos, refresh } = usePhotoFeed(config.galleryPollIntervalSeconds);
  const isUploadingRef = useRef(false);
  const isLightboxOpenRef = useRef(false);
  const isSavingRef = useRef(false);

  // "Recharger les pages invités" in the admin Maintenance menu - same safety
  // rules as the periodic reload below: never interrupts an upload, the
  // lightbox or a save-to-device (it just waits and retries).
  useReloadOnToken(
    config.guestReloadToken,
    () => !isUploadingRef.current && !isLightboxOpenRef.current && !isSavingRef.current,
  );

  // Periodic full reload keeps long-running phone sessions fresh (clears any
  // accumulated JS state, picks up newly deployed versions) without needing
  // the gallery poll to do everything. Skipped whenever a photo is actively
  // being sent, the lightbox is open, or a save-to-device is in progress
  // (someone's mid-upload, mid-browse, or mid-download) — it just retries on
  // the next tick instead of interrupting them.
  useEffect(() => {
    const interval = setInterval(() => {
      if (isUploadingRef.current || isLightboxOpenRef.current || isSavingRef.current) return;
      window.location.reload();
    }, config.guestPageReloadIntervalSeconds * 1000);
    return () => clearInterval(interval);
  }, [config.guestPageReloadIntervalSeconds]);

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <header className={styles.header}>
          <h1>{config.coupleNames}</h1>
          {config.weddingDate && <p className={styles.weddingDate}>{config.weddingDate}</p>}
          <p className={styles.subtitle}>Partagez vos plus beaux souvenirs ✨</p>
          <Link to="/livredor" className={styles.guestbookLink}>
            <span className={styles.ctaTitle}>📖 Le livre d'or</span>
            <span className={styles.ctaText}>Laisse-nous un petit mot pour immortaliser ce moment</span>
          </Link>
        </header>

        <section className={styles.uploadSection}>
          {config.uploadsPaused ? (
            <p className={styles.pausedNotice}>
              Les envois sont momentanément en pause — la galerie reste consultable.
            </p>
          ) : (
            <UploadDropzone
              onUploaded={refresh}
              onUploadingChange={(uploading) => {
                isUploadingRef.current = uploading;
              }}
            />
          )}
        </section>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>La galerie ({photos.length})</h2>
        <PhotoGrid
          photos={photos}
          coupleNames={config.coupleNames}
          weddingDate={config.weddingDate}
          onLightboxOpenChange={(open) => {
            isLightboxOpenRef.current = open;
          }}
          onSavingChange={(saving) => {
            isSavingRef.current = saving;
          }}
        />
      </section>
    </div>
  );
}
